/**
 * Account Main Layout Web Component
 * Handles the main layout structure for account dashboard
 */

// Import Ordergroove API helper functions
import {
  getOGSubscriptions,
  getOGOrdersWithStatus,
  getOGItems,
  changeOGSubscriptionQuantity,
  changeOGSubscriptionFrequency,
  changeOGItemQuantity,
  reactivateOGSubscription,
  sendOGOrderNow,
  getOGProduct,
  skipOGOrder,
  getAllOrderItems,
  getOGShippingAddresses,
  getOGCountries,
  getOGPaymentInfo,
  removeItemFromNextBox,
  updateShippingAddress,
  updateAllSubscriptionShippingAddresses,
  addSubToNextBox,
  normalizeOrderDateForOgApi,
  sendPaymentUpdateEmail,
  getAllSwappableItemsForProduct,
  changeSubDeliveryDate,
} from '../subscription-modal/ordergroove-api-helper';

// Import timezone helper functions
import {
  calculateEditDeadline,
  calculateProcessingThreshold,
  isBeforeEditDeadline,
  isProcessingSoon,
  isProcessing,
  parseOgPlaceDateUtc,
} from '../../utils/timezone-helpers.js';
import { pushDscDataLayerEvent } from '../../utils/datalayer.js';

export default function accountMainLayout() {
  return {
    customerData: null,
    allSubscriptions: [],
    nextOrders: [],
    nextOrderItems: [],
    activeSubscriptions: [],
    inactiveSubscriptions: [],
    trySomethingNewItems: [],
    bestSellersItems: [],
    loading: true,
    swappableItemsLoading: true,
    currentModal: null,
    lastFocusedElement: null,
    allProductData: [],
    activeSubscriptionsLoading: false,
    inactiveSubscriptionsLoading: false,
    nextOrderLoading: false,
    allOrderItems: [],
    dateLoading: false,
    changeDateLoading: false,
    allShippingAddresses: [],
    nextOrderShippingAddress: null,
    shippingAddressErrors: {},
    shippingAddressFormError: '',
    isSavingAddress: false,
    nextOrderPaymentInfo: null,
    countryData: {},
    addingToNextBoxLoading: false,
    orderDetailsLoading: false,
    order: null,
    modalOpen: null,
    editNextBoxDeadLine: null,
    processingThreshold: null,
    currentTime: new Date(), // Reactive property for current time
    clubBenefitsSubActionTaken: window.dsc?.clubBenefitsSubActionTaken ?? false,

    // Request deduplication
    _inflightRequests: new Map(),
    _debounceTimers: new Map(),

    /**
     * Deduplicate API requests - returns existing promise if request is in-flight
     * @param {string} key - Unique identifier for the request
     * @param {Function} apiCall - The API function to call
     * @returns {Promise} - The API response
     */
    async _deduplicateRequest(key, apiCall) {
      // Check if request is already in-flight
      if (this._inflightRequests.has(key)) {
        return this._inflightRequests.get(key);
      }

      // Create new request
      const promise = apiCall().finally(() => {
        // Remove from in-flight map when complete
        this._inflightRequests.delete(key);
      });

      // Store in-flight request
      this._inflightRequests.set(key, promise);
      return promise;
    },

    /**
     * Debounce a function call
     * @param {string} key - Unique identifier for the debounced function
     * @param {Function} fn - Function to debounce
     * @param {number} delay - Delay in milliseconds (default: 300ms)
     */
    _debounce(key, fn, delay = 300) {
      // Clear existing timer
      if (this._debounceTimers.has(key)) {
        clearTimeout(this._debounceTimers.get(key));
      }

      // Set new timer
      const timer = setTimeout(() => {
        this._debounceTimers.delete(key);
        fn();
      }, delay);

      this._debounceTimers.set(key, timer);
    },

    /** OrderGroove amounts may omit trailing zeros (e.g. "4"); always show two decimals in UI. */
    formatUsdTwoDecimals(value) {
      const normalized = String(value ?? '')
        .replace(/,/g, '')
        .replace(/^\s*\$\s*/, '')
        .trim();
      const n = parseFloat(normalized);
      if (Number.isNaN(n)) return '0.00';
      return n.toFixed(2);
    },

    /**
     * True when the next order already has a line with the same product + offer as this subscription.
     * Requires both sides to have a non-empty offer so we never treat undefined===undefined as a match
     * (cached subscriptions used to omit offer and hid "Add to next box" incorrectly).
     */
    nextOrderHasDuplicateProductOfferLine(item) {
      if (!item?.productObj?.external_product_id || !this.nextOrderItems?.length) return false;
      const productId = item.productObj.external_product_id;
      const subOffer = item.offer;
      const subOfferDefined = subOffer != null && String(subOffer).trim() !== '';
      return this.nextOrderItems.some((orderItem) => {
        if (orderItem.productObj?.external_product_id !== productId) return false;
        if (!subOfferDefined) return false;
        const lineOffer = orderItem.offer;
        const lineOfferDefined = lineOffer != null && String(lineOffer).trim() !== '';
        return lineOfferDefined && subOffer === lineOffer;
      });
    },

    init() {
      // Fetch and log customer information
      this.fetchCustomerInfo();

      // Update current time every minute to trigger reactive UI updates
      // This ensures deadline messages update automatically
      setInterval(() => {
        this.currentTime = new Date();
      }, 60000); // Update every 60 seconds

      const initialOrderId = new URLSearchParams(window.location.search).get('orderId');
      if (
        initialOrderId &&
        (window.location.hash === '#order-details' || !window.location.hash)
      ) {
        this.showOrderDetails('', initialOrderId);
      }

      // Add a global listener to handle modal closures from any source
      window.addEventListener('account-modal-closed', () => {
        this.modalOpen = null;
      });

      // Keep club benefits banner in sync when data is broadcast (e.g. after subscription action)
      document.addEventListener('club-benefits-data-ready', (e) => {
        this.clubBenefitsSubActionTaken = e.detail?.clubBenefitsSubActionTaken ?? this.clubBenefitsSubActionTaken;
      });
    },

    // Helper function to sort subscriptions by a date key
    _sortSubscriptionsByDate(subscriptions, dateKey, direction) {
      return subscriptions.sort((a, b) => {
        const dateA =
          typeof a[dateKey] === 'string' && a[dateKey] ? parseOgPlaceDateUtc(a[dateKey]) : null;
        const dateB =
          typeof b[dateKey] === 'string' && b[dateKey] ? parseOgPlaceDateUtc(b[dateKey]) : null;

        if (dateA === null && dateB === null) return 0;

        // Handle nulls by placing them at the end of the sorted list
        if (dateA === null) return 1;
        if (dateB === null) return -1;

        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      });
    },

    _convertToUTCDate(dateInput) {
      const localDate = dateInput instanceof Date ? dateInput : new Date(dateInput);
      // Creates a new Date object using UTC values from the localDate
      return new Date(
        Date.UTC(
          localDate.getFullYear(),
          localDate.getMonth(),
          localDate.getDate(),
          localDate.getHours(),
          localDate.getMinutes(),
          localDate.getSeconds(),
          localDate.getMilliseconds(),
        ),
      );
    },

    /**
     * Calculate the edit deadline: 8:00 PM Pacific Time, one day before order processes
     * Uses timezone-aware calculation to handle DST correctly
     */
    _calculateEditDeadline() {
      if (this.nextOrders.length > 0 && this.nextOrders[0].place) {
        return calculateEditDeadline(this.nextOrders[0].place);
      }
      return null;
    },

    /**
     * Calculate the processing threshold: 3 hours before the order processes
     * Note: Currently unused but kept for potential future features
     */
    _calculateProcessingThreshold() {
      if (this.nextOrders.length > 0 && this.nextOrders[0].place) {
        return calculateProcessingThreshold(this.nextOrders[0].place);
      }
      return null;
    },

    _broadcastClubBenefitsData() {
      // Count active subscriptions (subscriptions without a cancelled date)
      const activeSubscriptionsCount = this.activeSubscriptions?.length || 0;

      // Store globally for other components
      window.dsc = window.dsc || {};
      window.dsc.activeSubscriptionsCount = activeSubscriptionsCount;
      window.dsc.clubBenefitsLoaded = true;

      // Keep Alpine state in sync for account banner
      this.clubBenefitsSubActionTaken = window.dsc.clubBenefitsSubActionTaken ?? false;

      // Dispatch event for reactive components
      document.dispatchEvent(
        new CustomEvent('club-benefits-data-ready', {
          detail: {
            activeSubscriptionsCount,
            clubBenefitsSubActionTaken: window.dsc.clubBenefitsSubActionTaken ?? false,
          },
        }),
      );
    },

    /**
     * Process next order items to generate summary data for the analytics/data layer.
     *
     * @param {Array<Object>} nextOrderItems - Array of items in the upcoming order.
     * @returns {{nextBoxSubtotal: string, nextBoxSubs: number, itemsCondensed: Array<Object>}} Aggregated order data.
     */
    _processNextOrderItemsForDataLayer(nextOrderItems) {
      const nextBoxSubtotal = nextOrderItems.reduce((sum, item) => sum + parseFloat(item.total_price), 0).toFixed(2);
      const nextBoxSubs = nextOrderItems.filter((item) => item.subscription !== null).length;
      const itemsCondensed = [];
      nextOrderItems.forEach((item) => {
        itemsCondensed.push({
          subscription_id: item.subscription,
          item_name: item.productObj.name,
          item_variant: item.productObj.name,
          item_variant_id: item.productObj.external_product_id,
          quantity: item.quantity,
          sub_frequency: item.every != null ? `${item.every} month${item.every > 1 ? 's' : ''}` : null,
          price: item.price,
        });
      });

      return { nextBoxSubtotal, nextBoxSubs, itemsCondensed };
    },

    /**
     * Computed property: Check if we're currently in the editable window
     * Using a getter so Alpine.js can reactively track this
     */
    get isBeforeEditDeadline() {
      // We logically AND the time with the function result.
      // Since this.currentTime is always truthy (a Date object),
      // this triggers the dependency without changing the result.
      return this.currentTime && isBeforeEditDeadline(this.editNextBoxDeadLine);
    },

    /**
     * Computed property: Check if we're in the "processing soon" window
     */
    get isProcessingSoon() {
      // Same logic: Access currentTime to trigger reactivity
      if (!this.currentTime) return false;

      const orderStatus = this.nextOrders.length > 0 ? this.nextOrders[0].status : null;
      const placeDate = this.nextOrders.length > 0 ? this.nextOrders[0].place : null;
      return isProcessingSoon(this.editNextBoxDeadLine, placeDate, orderStatus);
    },

    /**
     * Computed property: Check if the order is currently processing
     * RETURNS TRUE IF:
     * 1. Server says status is 6 (Processing)
     * OR
     * 2. It is strictly past 10 PM PT on the day before (Time Lock)
     */
    get isProcessing() {
      // 1. Check Server Status (The Source of Truth)
      const orderStatus = this.nextOrders.length > 0 ? this.nextOrders[0].status : null;

      if (isProcessing(orderStatus)) return true;

      // 2. Check Time Lock
      if (this.editNextBoxDeadLine) {
        const processingStartTime = new Date(this.editNextBoxDeadLine.getTime() + 2 * 60 * 60 * 1000);

        // We use this.currentTime here in the comparison, so the linter is happy
        const now = this.currentTime;

        if (now >= processingStartTime) return true;
      }

      return false;
    },

    //initialize the app
    async fetchCustomerInfo() {
      // Prevent duplicate calls
      if (window._accountMainLayoutFetching) {
        return;
      }
      window._accountMainLayoutFetching = true;

      if (!window.dsc || !window.dsc.customerId || window.dsc.customerId === 'null') {
        this.loading = false;
        this.swappableItemsLoading = false;
        window._accountMainLayoutFetching = false;
        return;
      }

      try {
        await window.dsc?.ensureSubscriberHydrated?.();
        this.activeSubscriptionsLoading = true;
        this.inactiveSubscriptionsLoading = true;

        // Load core data needed to render the page
        const [subscriptions, nextOrdersResponse, allOrderItemsResponse] = await Promise.all([
          this._deduplicateRequest(`getOGSubscriptions-${window.dsc.customerId}`, () =>
            getOGSubscriptions(window.dsc.customerId),
          ),
          this._deduplicateRequest('getOGOrdersWithStatus', () => getOGOrdersWithStatus()),
          this._deduplicateRequest('getAllOrderItems', () => getAllOrderItems()),
        ]);

        this.allSubscriptions = subscriptions?.results || [];
        this.subscribedProducts = subscriptions?.results?.map((sub) => sub.product) || [];
        this.nextOrders = nextOrdersResponse?.results || [];
        this.allOrderItems = allOrderItemsResponse?.results || [];
        this.trySomethingNewItems = window.dsc.trySomethingNewVariantsById || [];
        this.bestSellersItems = window.dsc.bestSellersVariantsById || [];

        // Get all product IDs
        const allProductIds = [
          ...this.trySomethingNewItems,
          ...this.bestSellersItems,
          ...this.subscribedProducts,
          ...this.allOrderItems.map((item) => item?.product),
        ];

        // Fetch product data for each unique product ID
        const uniqueProductIds = [...new Set(allProductIds)].filter(Boolean);
        const productPromises = uniqueProductIds.map((id) => getOGProduct(id));
        const productData = await Promise.all(productPromises);

        // Initialize products with empty swappableItems arrays
        this.allProductData = productData.filter(Boolean).map((product) => ({
          ...product,
          swappableItems: [], // Will be populated in background
        }));

        // Calculate timezone-aware deadlines
        this.editNextBoxDeadLine = this._calculateEditDeadline();
        this.processingThreshold = this._calculateProcessingThreshold();

        // Build next order items
        const nextOrderID = this.nextOrders.length > 0 ? this.nextOrders[0].public_id : null;
        const nextPlaceDate = this.nextOrders.length > 0 ? this.nextOrders[0].place : null;
        const nextOrderStatus = this.nextOrders.length > 0 ? this.nextOrders[0].status : null;
        const filteredItems = this.allOrderItems.filter(
          (item) => nextOrderID != null && String(item.order) === String(nextOrderID),
        );

        this.nextOrderItems = filteredItems.map((item) => ({
          ...item,
          productObj: this.allProductData.find((x) => x.external_product_id === item.product),
          place: nextPlaceDate,
          status: nextOrderStatus,
          every: (() => {
            const sub = this.allSubscriptions.find((subscription) => subscription.public_id === item.subscription);
            return sub ? sub.every : undefined;
          })(),
        }));

        this.nextOrderSubscriptions = this.nextOrderItems.map((item) => item.subscription) || [];

        // Get subscription product data
        this.allSubscriptions = this.allSubscriptions.map((sub) => {
          if (sub.cancelled && typeof sub.cancelled === 'string') {
            const cancelledDate = parseOgPlaceDateUtc(sub.cancelled);
            if (cancelledDate) {
              sub.cancelledDateFormatted = cancelledDate.toLocaleString('default', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              });
            }
          }

          const subscribedProduct = this.allProductData.find((product) => product.external_product_id === sub.product);
          if (subscribedProduct) {
            subscribedProduct.currency_code = sub.currency_code;
            sub.productObj = subscribedProduct;
            sub.isInNextBox =
              this.nextOrderSubscriptions.includes(sub.public_id) &&
              this.nextOrderItems.some(
                (item) =>
                  item.productObj?.external_product_id === sub.productObj?.external_product_id &&
                  item.subscription === sub.public_id,
              );
            sub.placeDateFormatted = null;

            const orderItem = this.allOrderItems.find(
              (item) => item.product === sub.product && item.subscription === sub.public_id,
            );

            if (orderItem) {
              sub.ogOrderId = orderItem.order;
              sub.total_price = parseFloat(orderItem.total_price).toFixed(2);
              sub.final_price = parseFloat(orderItem.total_price).toFixed(2);
              const nextOrder = this.nextOrders.find((order) => order.public_id === orderItem.order);

              if (nextOrder && nextOrder.place) {
                const placeDate = parseOgPlaceDateUtc(nextOrder.place);
                if (placeDate) {
                  sub.placeDateRaw = nextOrder.place;
                  sub.placeDateFormatted = placeDate.toLocaleString('default', {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  });
                }
              }
            } else {
              sub.total_price = (parseFloat(sub.productObj.price) * parseInt(sub.quantity)).toFixed(2);
              sub.final_price = (parseFloat(sub.productObj.price) * parseInt(sub.quantity)).toFixed(2);
            }
            sub.final_unit_price = (parseFloat(sub.final_price) / parseInt(sub.quantity)).toFixed(2);
          }

          return sub;
        });

        if (this.allSubscriptions.length > 0) {
          const activeSubs = this.allSubscriptions.filter((sub) => sub.live === true);
          this.activeSubscriptions = this._sortSubscriptionsByDate(activeSubs, 'placeDateRaw', 'asc');

          const inactiveSubs = this.allSubscriptions.filter((sub) => sub.live === false);
          this.inactiveSubscriptions = this._sortSubscriptionsByDate(inactiveSubs, 'cancelled', 'desc');
        }

        // Broadcast club benefits data for other components
        this._broadcastClubBenefitsData();

        // Expose subscription ship dates for ProsperStack fallback when their payload lacks next_charge_date
        this._updateSubscriptionShipDatesCache();

        this.activeSubscriptionsLoading = false;
        this.inactiveSubscriptionsLoading = false;
        this.loading = false;
        this.nextOrderLoading = false;
        window._accountMainLayoutFetching = false;

        // Load background data like the swappable items, shipping addresses, countries, payment info
        this._loadOGBackgroundData();
      } catch (error) {
        this.loading = false;
        this.swappableItemsLoading = false;
        this.activeSubscriptionsLoading = false;
        this.inactiveSubscriptionsLoading = false;
        window._accountMainLayoutFetching = false;
        console.error(`Ordergroove: Failed to fetch customer info: ${error.message}`);
      }
    },

    /**
     * Load data in the background after the page is visible (Saves approximately 1 second of loading time)
     * Includes: swappable items, shipping addresses, countries, payment info
     */
    _refreshProductObjRefsFromAllProductData() {
      const productObjFor = (productId) =>
        this.allProductData.find((x) => x.external_product_id === productId) || null;
      this.nextOrderItems = this.nextOrderItems.map((item) => ({
        ...item,
        productObj: productObjFor(item.product),
      }));
      this.allSubscriptions = this.allSubscriptions.map((sub) => ({
        ...sub,
        productObj: productObjFor(sub.product),
      }));
      this.activeSubscriptions = this.activeSubscriptions.map((sub) => ({
        ...sub,
        productObj: productObjFor(sub.product),
      }));
      this.inactiveSubscriptions = this.inactiveSubscriptions.map((sub) => ({
        ...sub,
        productObj: productObjFor(sub.product),
      }));
    },

    async _loadSwappableItemsForAllProducts() {
      if (!this.allProductData?.length) return;

      const groupNameToProducts = new Map();

      for (const product of this.allProductData) {
        if (!Array.isArray(product.groups)) {
          product.swappableItems = [];
          continue;
        }
        const swapGroup = product.groups.find((g) => g.group_type === 'sku_swap');
        const rawGroupName = swapGroup?.name;
        const groupName = typeof rawGroupName === 'string' ? rawGroupName.trim() : rawGroupName;
        if (!groupName) {
          product.swappableItems = [];
          continue;
        }
        product.swappableItems = [];
        if (!groupNameToProducts.has(groupName)) {
          groupNameToProducts.set(groupName, []);
        }
        groupNameToProducts.get(groupName).push(product);
      }

      const groupNameToItems = new Map();
      await Promise.all(
        Array.from(groupNameToProducts.keys()).map(async (groupName) => {
          try {
            const items = (await getAllSwappableItemsForProduct(groupName)) || [];
            groupNameToItems.set(groupName, items);
          } catch (error) {
            groupNameToItems.set(groupName, []);
          }
        }),
      );

      for (const [groupName, products] of groupNameToProducts.entries()) {
        const items = groupNameToItems.get(groupName) || [];
        for (const product of products) {
          product.swappableItems = items;
        }
      }

      this._refreshProductObjRefsFromAllProductData();
    },

    async _loadOGBackgroundData() {
      try {
        const [shippingAddressesResponse, countryResponse] = await Promise.all([
          this._deduplicateRequest('getOGShippingAddresses', () => getOGShippingAddresses()),
          getOGCountries(),
        ]);

        this.allShippingAddresses = shippingAddressesResponse?.data?.results || [];
        if (this.nextOrders.length > 0) {
          this.nextOrderShippingAddress = this.allShippingAddresses.find(
            (address) => address.public_id === this.nextOrders[0].shipping_address,
          );
        }

        this.countryData = countryResponse?.data || {};

        this.$nextTick(() => {
          const countrySelect = document.getElementById('AddressCountryNew');
          if (countrySelect && countrySelect.value) {
            this.handleCountryChange({ target: { value: countrySelect.value } }, this.nextOrderShippingAddress);
          }
        });

        if (this.nextOrders.length > 0 && this.nextOrders[0].payment) {
          const paymentInfoResponse = await getOGPaymentInfo(this.nextOrders[0].payment);
          this.nextOrderPaymentInfo = paymentInfoResponse?.data || null;
        }

        await this._loadSwappableItemsForAllProducts();

        this.swappableItemsLoading = false;
        window._accountMainLayoutFetching = false;
      } catch (error) {
        this.swappableItemsLoading = false;
        window._accountMainLayoutFetching = false;
        console.error('Failed to load background data:', error);
      }
    },

    //updateNextOrderObject - updates the next order object and next order items
    async updateNextOrderObject(isDateLoading = false) {
      this.nextOrderLoading = true;
      this.dateLoading = isDateLoading;
      const nextOrdersResponse = (await getOGOrdersWithStatus()) || [];
      this.nextOrders = nextOrdersResponse.results;
      if (nextOrdersResponse.results.length === 0) {
        this.nextOrders = [];
        this.nextOrderItems = [];
        this.nextOrderSubscriptions = [];
        this.loading = false;
        return;
      }
      if (this.nextOrders[0].shipping_address) {
        this.nextOrderShippingAddress = this.allShippingAddresses.find(
          (address) => address.public_id === this.nextOrders[0].shipping_address,
        );
      }

      //set next order items options

      const OGItemsOptions = {
        orderId: this.nextOrders[0]?.public_id ? this.nextOrders[0]?.public_id : null,
        products: this.allProductData ? this.allProductData : [],
        placeDate: this.nextOrders[0]?.place ? this.nextOrders[0]?.place : null,
        status: this.nextOrders[0]?.status ? this.nextOrders[0]?.status : null,
        subscriptions: this.allSubscriptions ? this.allSubscriptions : [],
      };
      const nextOrderItemsResponse = await getOGItems(OGItemsOptions);

      // Recalculate timezone-aware deadlines
      this.editNextBoxDeadLine = this._calculateEditDeadline();
      this.processingThreshold = this._calculateProcessingThreshold();

      if (nextOrderItemsResponse.length === 0) {
        this.nextOrderItems = [];
        this.loading = false;
        this.nextOrderLoading = false;
        this.dateLoading = false;
        return;
      }
      this.nextOrderItems = nextOrderItemsResponse.map((item) => {
        return {
          ...item,
        };
      });
      await this.updateSubscriptionsWithNewOrderInfo();
      this._updateSubscriptionShipDatesCache();
      this.nextOrderLoading = false;
      this.dateLoading = false;
    },

    /**
     * Populate window.dsc.subscriptionShipDates for ProsperStack fallback when its payload lacks ship date
     */
    _updateSubscriptionShipDatesCache() {
      window.dsc = window.dsc || {};
      window.dsc.subscriptionShipDates = {};
      this.activeSubscriptions?.forEach((sub) => {
        if (sub.placeDateRaw && sub.public_id) {
          window.dsc.subscriptionShipDates[sub.public_id] = sub.placeDateRaw;
        }
      });
      this.nextOrderItems?.forEach((item) => {
        if (item.place && item.subscription) {
          window.dsc.subscriptionShipDates[item.subscription] = item.place;
        }
      });
    },

    /**
     * Dispatch subscription-next-date-changed for Visually/subscription bar.
     */
    _broadcastNextBoxDateChanged(source) {
      const nextBoxPlaceDate = this.nextOrders?.[0]?.place ?? null;
      window.dispatchEvent(
        new CustomEvent('subscription-next-date-changed', {
          detail: { nextBoxPlaceDate, source },
        }),
      );
    },

    //updates one sub date
    async updateSubscriptionDate(subscriptionId, date) {
      this.changeDateLoading = true;
      const subToEdit = this.activeSubscriptions.find((x) => x.public_id === subscriptionId);
      if (!subToEdit) {
        return;
      }

      const [year, month, day] = date.split('-').map(Number);
      const formattedDate = new Date(Date.UTC(year, month - 1, day));

      if (isNaN(formattedDate.getTime())) {
        return;
      }

      // Step 1: Update the date for the specific subscription being edited.
      subToEdit.placeDateRaw = `${date} 00:00:00`;
      subToEdit.placeDateFormatted = formattedDate.toLocaleString('default', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });

      // --- Logic to correctly identify the new "next box" ---
      await this.updateNextOrderObject();

      this._broadcastNextBoxDateChanged('edit_sub_date');

      window.dispatchEvent(new CustomEvent('refresh-active-slider'));

      this.changeDateLoading = false;

      this.$dispatch('toast', { message: window.translations.customer.global.date_updated, type: 'success' });
    },

    //remove from active subscriptions and updates them
    async updateSubscriptionsWhenRemove(sub) {
      const subToPush = this.activeSubscriptions.find(
        (x) => x.public_id === sub.subscription || x.public_id === sub.public_id,
      );
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      subToPush.cancelled = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())} ${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`;
      subToPush.cancelledDateFormatted = now.toLocaleString('default', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
      this.inactiveSubscriptions.unshift(subToPush);
      this.activeSubscriptions = this.activeSubscriptions.filter((x) => x.public_id !== subToPush.public_id);
      this._broadcastClubBenefitsData();
      await this.updateSubscriptionsWithNewOrderInfo();
      if (this.nextOrderItems.length === 1 && this.nextOrders.length === 1) {
        window.history.replaceState(null, null, '/account#subscriptions');
        window.dispatchEvent(new CustomEvent('change-tab'));
      }
    },

    async updateSubscriptionDates(orderId, date) {
      const preliminarySubscriptions = this.activeSubscriptions.map((sub) => {
        if (sub.ogOrderId === orderId) {
          return {
            ...sub,
            placeDateRaw: date,
            placeDateFormatted: date
              ? parseOgPlaceDateUtc(date)?.toLocaleString('default', {
                  month: 'short',
                  day: 'numeric',
                  timeZone: 'UTC',
                }) ?? null
              : null,
          };
        }
        return sub;
      });

      let nextOrderId = null;
      let earliestDate = null;

      preliminarySubscriptions.forEach((sub) => {
        if (sub.placeDateRaw) {
          const subDate = parseOgPlaceDateUtc(sub.placeDateRaw);
          if (subDate && (!earliestDate || subDate < earliestDate)) {
            earliestDate = subDate;
            nextOrderId = sub.ogOrderId;
          }
        }
      });

      const finalSubscriptions = preliminarySubscriptions.map((sub) => ({
        ...sub,
        isInNextBox: sub.ogOrderId === nextOrderId,
      }));

      // Re-assign the array to trigger an instant UI update in Alpine.
      this.activeSubscriptions = finalSubscriptions;

      // sort subs by date
      this.activeSubscriptions = this._sortSubscriptionsByDate(this.activeSubscriptions, 'placeDateRaw', 'asc');

      await this.updateNextOrderObject();

      this._broadcastNextBoxDateChanged('edit_order_date');
    },

    async refreshCustomerInfo() {
      this.loading = true;
      this.swappableItemsLoading = true;
      await this.fetchCustomerInfo();
    },

    //uses new order info to update if in next box
    async updateSubscriptionsWithNewOrderInfo() {
      const nextOrder = this.nextOrders[0];

      if (nextOrder) {
        const nextOrderShipping = nextOrder.shipping_address;
        const normalizedNextOrderPlace = nextOrder.place
          ? parseOgPlaceDateUtc(nextOrder.place)?.toISOString().slice(0, 10) ?? null
          : null;

        this.activeSubscriptions.forEach((sub) => {
          const subShipping = sub.shipping_address;
          const normalizedSubPlace =
            typeof sub.placeDateRaw === 'string' && sub.placeDateRaw
              ? parseOgPlaceDateUtc(sub.placeDateRaw)?.toISOString().slice(0, 10) ?? null
              : null;

          sub.isInNextBox =
            subShipping === nextOrderShipping &&
            normalizedSubPlace === normalizedNextOrderPlace &&
            this.nextOrderItems.some(
              (item) => item.productObj?.external_product_id === sub.productObj?.external_product_id,
            );
        });
      } else {
        // If there are no next orders, nothing is in the next box
        this.activeSubscriptions.forEach((sub) => {
          sub.isInNextBox = false;
        });
      }

      // Step 4: Finally, sort all subscriptions by date for correct display order.
      this.activeSubscriptions.sort((a, b) => {
        const dateA =
          typeof a.placeDateRaw === 'string' && a.placeDateRaw
            ? parseOgPlaceDateUtc(a.placeDateRaw) ?? new Date('9999-12-31')
            : new Date('9999-12-31');
        const dateB =
          typeof b.placeDateRaw === 'string' && b.placeDateRaw
            ? parseOgPlaceDateUtc(b.placeDateRaw) ?? new Date('9999-12-31')
            : new Date('9999-12-31');
        return dateA - dateB;
      });
    },

    async updateSwappedProduct(subscriptionId, newProduct) {
      if (!newProduct || !newProduct.external_product_id) {
        return;
      }

      const subscription = this.activeSubscriptions.find((sub) => sub.public_id === subscriptionId);
      const nextOrderItem = this.nextOrderItems.find((item) => item.subscription === subscriptionId);
      if (!subscription) {
        return;
      }

      // Use the ID from the newProduct object for the lookup
      let updateProduct = this.allProductData.find((p) => p.external_product_id === newProduct.external_product_id);

      if (!updateProduct) {
        // If not found, fetch it, add it to our data, and then assign it
        const fetchedProduct = await getOGProduct(newProduct.external_product_id);
        if (fetchedProduct) {
          // enrich the product with swappable items
          if (Array.isArray(fetchedProduct.groups)) {
            const swapGroup = fetchedProduct.groups.find((g) => g.group_type === 'sku_swap');
            if (swapGroup && swapGroup.name) {
              try {
                const swappableItems = await getAllSwappableItemsForProduct(swapGroup.name);
                fetchedProduct.swappableItems = swappableItems || [];
              } catch (error) {
                fetchedProduct.swappableItems = [];
              }
            } else {
              fetchedProduct.swappableItems = [];
            }
          } else {
            fetchedProduct.swappableItems = [];
          }
          this.allProductData.push(fetchedProduct);
          updateProduct = fetchedProduct;
        } else {
          return; // Exit if we can't get the new product info
        }
      }

      // Assign the complete, correct product object to the subscription
      if (subscription && updateProduct) {
        Object.assign(subscription, {
          productObj: updateProduct,
          price: parseFloat(updateProduct.price * subscription.quantity).toFixed(2),
          final_price: parseFloat(updateProduct.price * subscription.quantity).toFixed(2),
          total_price: parseFloat(updateProduct.price * subscription.quantity).toFixed(2),
          product: updateProduct.external_product_id,
          final_unit_price: parseFloat(updateProduct.price).toFixed(2),
        });
      }
      const nextOrder = this.nextOrders && this.nextOrders[0];
      if (nextOrderItem && updateProduct && nextOrder && nextOrder.status !== 6) {
        Object.assign(nextOrderItem, {
          productObj: updateProduct,
          price: parseFloat(updateProduct.price * nextOrderItem.quantity).toFixed(2),
          total_price: parseFloat(updateProduct.price * nextOrderItem.quantity).toFixed(2),
          product: updateProduct.external_product_id,
        });
      }

      this.updateSubscriptionsWithNewOrderInfo();
    },

    async saveAddressChanges(address, updateAll) {
      this.isSavingAddress = true;
      this.shippingAddressFormError = ''; // Clear previous form error
      this.shippingAddressErrors = {}; // Clear previous errors

      if (!address.first_name)
        this.shippingAddressErrors.first_name = window.translations.customer.addresses.errors.first_name_required;
      if (!address.last_name)
        this.shippingAddressErrors.last_name = window.translations.customer.addresses.errors.last_name_required;
      if (!address.address)
        this.shippingAddressErrors.address = window.translations.customer.addresses.errors.address_required;
      if (!address.city) this.shippingAddressErrors.city = window.translations.customer.addresses.errors.city_required;
      if (!address.country_code)
        this.shippingAddressErrors.country = window.translations.customer.addresses.errors.country_required;
      if (!address.state_province_code)
        this.shippingAddressErrors.state_province_code =
          window.translations.customer.addresses.errors.province_required;
      if (!address.zip_postal_code)
        this.shippingAddressErrors.zip_postal_code = window.translations.customer.addresses.errors.zip_required;

      if (Object.keys(this.shippingAddressErrors).length > 0) {
        // Add a small delay to allow Alpine to update the UI with error messages
        this.$nextTick(() => {
          const firstErrorField = document.querySelector('.form__error-message.has-error');
          if (firstErrorField) {
            firstErrorField.closest('.field_address')?.querySelector('input, select')?.focus();
          }
          this.isSavingAddress = false;
        });
        return; // Stop if there are errors
      }

      let shippingAddressUpdateResponse;
      if (updateAll) {
        shippingAddressUpdateResponse = await updateAllSubscriptionShippingAddresses(address);
        if (shippingAddressUpdateResponse.data && shippingAddressUpdateResponse.data.public_id) {
          this.activeSubscriptions = this.activeSubscriptions.map((sub) => {
            return {
              ...sub,
              shipping_address: shippingAddressUpdateResponse.data.public_id,
            };
          });
        }
        await this.updateNextOrderObject();
      } else {
        shippingAddressUpdateResponse = await updateShippingAddress(address, this.nextOrders[0].public_id);
      }

      if (!shippingAddressUpdateResponse.success) {
        this.shippingAddressFormError = window.translations.customer.global.unexpected_error;
        this.isSavingAddress = false;
        return;
      }

      if (updateAll) {
        this.$dispatch('toast', {
          message: window.translations.customer.global.all_addresses_changed,
          type: 'success',
        });
      } else {
        this.$dispatch('toast', {
          message: window.translations.customer.global.address_changed_successfully,
          type: 'success',
        });
      }

      this.nextOrderShippingAddress = address;
      this.formattedShippingAddress(address.public_id);
      // Close the modal on successful save
      this.closeModal('account-shipping');
      this.isSavingAddress = false;
    },

    // Handle country change and update province options
    handleCountryChange(event, editableAddress = null) {
      const countryCode = event.target.value;
      const provinceSelect = document.getElementById('AddressProvinceNew');

      if (!provinceSelect) {
        return;
      }

      if (!this.countryData) {
        return;
      }

      // Clear existing options
      provinceSelect.innerHTML = '';

      // Get regions for selected country
      const country = this.countryData[countryCode];

      if (!country) {
        const provinceContainer = document.getElementById('AddressProvinceContainerNew');
        if (provinceContainer) {
          provinceContainer.style.display = 'none';
        }
        return;
      }

      if (!country.regions || !country.regions.length) {
        document.getElementById('AddressProvinceContainerNew').style.display = 'none';
        return;
      }

      // Show province container
      const provinceContainer = document.getElementById('AddressProvinceContainerNew');
      if (provinceContainer) {
        provinceContainer.style.display = 'flex';
      }

      // Add regions as options
      country.regions.forEach((region) => {
        const option = document.createElement('option');
        option.value = region.code;
        option.textContent = region.name;
        provinceSelect.appendChild(option);
      });

      // Set default value if available (check editableAddress first, then nextOrderShippingAddress)
      const currentProvinceCode =
        editableAddress?.state_province_code || this.nextOrderShippingAddress?.state_province_code;
      if (currentProvinceCode && provinceSelect) {
        provinceSelect.value = currentProvinceCode;
      }
    },

    // Send Now - Triggers immediate order placement
    async sendOrderNow(orderId) {
      // Track send now confirm
      const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';
      const sendNowData = window.dsc?.sendNowData;

      if (sendNowData) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_order_send_now_confirm',
          DSC: {
            location: locationHash,
            ship_date: sendNowData.ship_date,
            ordergroove_public_order_id: sendNowData.ordergroove_public_order_id,
            subtotal_amount: sendNowData.subtotal_amount,
            total_item_quantity: sendNowData.total_item_quantity,
            total_subscription_item_quantity: sendNowData.total_subscription_item_quantity,
            items: sendNowData.items,
          },
        });
      } else {
        // Do not block the send now action if tracking data is missing
        console.warn('sendNowData is missing; skipping dataLayer tracking for send now.');
      }

      if (!orderId) {
        alert(window.translations.customer.alerts.no_order_to_send);
        return;
      }

      try {
        const result = await sendOGOrderNow(orderId);
        if (result.success) {
          // Optionally refresh the order data
          await this.updateNextOrderObject();

          // update subscription dates
          this.activeSubscriptions.forEach((sub) => {
            if (sub.ogOrderId === orderId) {
              sub.isInNextBox = true;
              sub.placeDateRaw = this.nextOrders[0].place;
              sub.placeDateFormatted = new Date(this.nextOrders[0].place).toLocaleString('default', {
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
              });
            }
          });
        } else {
          alert(
            window.translations.customer.alerts.send_order_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
        }
      } catch (error) {
        throw new Error('Error sending order now:', error);
      }
    },

    async skipOrder(order_id) {
      // Track skip confirmation click
      const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';
      const skipOrderData = window.dsc.skipOrderData;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'dl_custom_order_skip_confirm',
        DSC: {
          location: locationHash,
          ship_date: skipOrderData.ship_date,
          ordergroove_public_order_id: skipOrderData.ordergroove_public_order_id,
          subtotal_amount: skipOrderData.subtotal_amount,
          total_item_quantity: skipOrderData.total_item_quantity,
          total_subscription_item_quantity: skipOrderData.total_subscription_item_quantity,
          items: skipOrderData.items,
        },
      });

      if (!order_id) {
        alert(window.translations.customer.alerts.no_order_to_skip);
        return;
      }

      try {
        const result = await skipOGOrder(order_id);

        if (result.success) {
          // Refresh the order data to show updated information
          this.updateOrderSkippedSubscriptionDates(order_id);
          await this.updateNextOrderObject();

          this._broadcastNextBoxDateChanged('skip');

          // Clear window.dsc.nextOrderShipDate if skip was successful
          window.dsc = window.dsc || {};
          window.dsc.skipOrderData = null;
        } else {
          alert(
            window.translations.customer.alerts.skip_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
        }
      } catch (error) {
        throw new Error('Error skipping subscription:', error);
      }
    },

    // changes date of skipped subs in order
    async updateOrderSkippedSubscriptionDates(order_id) {
      this.allSubscriptions.forEach((sub) => {
        if (sub.ogOrderId === order_id) {
          if (sub.frequency_days) {
            const newDate = parseOgPlaceDateUtc(sub.placeDateRaw);
            if (newDate) {
              newDate.setUTCDate(newDate.getUTCDate() + parseInt(sub.frequency_days, 10));
              const yyyy = newDate.getUTCFullYear();
              const mm = String(newDate.getUTCMonth() + 1).padStart(2, '0');
              const dd = String(newDate.getUTCDate()).padStart(2, '0');
              sub.placeDateRaw = `${yyyy}-${mm}-${dd} 00:00:00`;
              sub.placeDateFormatted = newDate.toLocaleString('default', {
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
              });
            }
          }
        }
      });
    },

    // when you skip a subscription
    async changeSubscriptionDeliveryDate(subscriptionId, currentDeliveryDate, context = null) {
      if (!subscriptionId) {
        alert(window.translations.customer.alerts.no_subscription_to_skip);
        return;
      }
      const activeSub = this.allSubscriptions.find((sub) => sub.public_id === subscriptionId);

      // Use provided date or fall back to subscription's current date
      const deliveryDate = currentDeliveryDate || activeSub?.place || activeSub?.placeDateRaw;

      const placeDate = parseOgPlaceDateUtc(deliveryDate);
      if (!placeDate) {
        console.warn('[account-main-layout] Could not parse delivery date for change:', deliveryDate);
        return;
      }
      placeDate.setUTCDate(placeDate.getUTCDate() + parseInt(activeSub.frequency_days, 10));
      const submissionDate = placeDate.toISOString();

      try {
        const result = await changeSubDeliveryDate(subscriptionId, submissionDate.split('T')[0]);

        if (result.success) {
          // Format the date back to the consistent 'YYYY-MM-DD HH:MM:SS' format for internal state
          const yyyy = placeDate.getUTCFullYear();
          const mm = String(placeDate.getUTCMonth() + 1).padStart(2, '0');
          const dd = String(placeDate.getUTCDate()).padStart(2, '0');
          // Refresh the order data to show updated information
          activeSub.placeDateRaw = `${yyyy}-${mm}-${dd} 00:00:00`;
          activeSub.placeDateFormatted = placeDate.toLocaleString('default', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          });
          activeSub.inNextBox = false;
          if (this.nextOrderItems.length > 1) {
            this.nextOrderItems = this.nextOrderItems.filter((item) => item.subscription !== subscriptionId);
          }
          await this.updateNextOrderObject();

          if (context === 'skip') {
            this.$dispatch('toast', {
              type: 'success',
              message: window.translations.customer.global.order_skipped,
              product: activeSub.productObj,
            });
          } else {
            this.$dispatch('toast', {
              type: 'success',
              message: window.translations.customer.global.date_updated,
              product: activeSub.productObj,
            });
          }
        } else {
          alert(
            window.translations.customer.alerts.skip_date_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
        }
      } catch (error) {
        throw new Error('Error skipping subscription delivery date:', error);
      }
    },

    //add Item to next box
    async addSubscriptionToNextBox(subscriptionObj) {
      if (!subscriptionObj) {
        alert(window.translations.customer.alerts.no_subscription_to_add);
        return;
      }

      // Capture original subscription ship date before it may be updated to next box date
      const originalShipDate = subscriptionObj.placeDateRaw ?? subscriptionObj.place ?? null;

      try {
        const rawDate = this.nextOrders[0].place;
        const formattedDateForAPI = normalizeOrderDateForOgApi(rawDate);
        const safariCompatibleDate = String(rawDate).includes(' ')
          ? String(rawDate).replace(' ', 'T')
          : String(rawDate);
        const displayDate = this._convertToUTCDate(safariCompatibleDate);

        const payload = {
          subscriptionId: subscriptionObj.public_id,
          shippingAddressId: this.nextOrders[0].shipping_address,
          date: formattedDateForAPI,
        };

        this.addingToNextBoxLoading = true;

        let result = await addSubToNextBox(payload);
        if (result.success) {
          const activeSub = this.activeSubscriptions.find((sub) => sub.public_id === subscriptionObj.public_id);
          if (activeSub) {
            activeSub.placeDateRaw = rawDate;
            let nextDate = displayDate;
            activeSub.placeDateFormatted = nextDate.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            });
            activeSub.shipping_address = this.nextOrders[0].shipping_address;
            activeSub.isInNextBox = true;
          }
          window.dispatchEvent(
            new CustomEvent('toast', {
              detail: {
                type: 'success',
                message: window.translations.customer.global.added_to_next_box,
                product: subscriptionObj.productObj,
              },
            }),
          );

          await this.updateNextOrderObject();
          if (window.location.hash !== '#next-box') {
            window.location.hash = '#next-box';
          }
          this.addingToNextBoxLoading = false;
        } else {
          this.addingToNextBoxLoading = false;
          alert(
            window.translations.customer.alerts.add_to_box_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
        }

        const hash = window.location.hash;
        const location =
          hash === '#next-box' ? 'other_subs' : hash === '#subscriptions' ? 'subs' : 'dashboard';
        const sub_frequency =
          subscriptionObj.every != null
            ? `${subscriptionObj.every} month${subscriptionObj.every > 1 ? 's' : ''}`
            : null;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_sub_ship_in_next_order',
          DSC: {
            location,
            old_ship_date: originalShipDate,
            subscription_id: subscriptionObj.public_id ?? null,
            item_name: subscriptionObj.productObj?.name ?? null,
            item_variant_id: subscriptionObj.productObj?.external_product_id ?? null,
            quantity: subscriptionObj.quantity ?? null,
            sub_frequency,
          },
        });
      } catch (error) {
        this.addingToNextBoxLoading = false;
        throw new Error('Error adding subscription to next box:', error);
      }
    },

    // Remove item from next box
    async removeItemFromNextBox(item, nextOrderItemCount) {
      const itemId = item.public_id;
      const subscriptionId = item.subscription;
      const place = item.place;
      const itemCount = nextOrderItemCount;

      // Show loading state
      this.nextOrderLoading = true;

      if (itemId) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dl_custom_sub_remove_confirm',
          DSC: {
            next_ship_date: item.place,
            item_count: itemCount,
            subscription_id: item.subscription || null,
            item_name: item.productObj?.name || null,
            item_variant_id: item.productObj?.external_product_id || null,
            quantity: item.quantity,
            sub_frequency: item.every != null ? `${item.every} month${item.every > 1 ? 's' : ''}` : null,
          },
        });

        try {
          const result = await removeItemFromNextBox(itemId);
          if (result.success) {
            //TODO: Show toast notification
            this.$dispatch('toast', {
              message: window.translations.customer.global.item_removed,
              type: 'success',
              product: this.nextOrderItems.find((item) => item.public_id === itemId).productObj,
            });
            // Update UI by filtering out the removed item
            this.nextOrderItems = this.nextOrderItems.filter((item) => item.public_id !== itemId);
            if (subscriptionId && place) {
              const activeSub = this.activeSubscriptions.find((sub) => sub.public_id === subscriptionId);
              if (activeSub) {
                activeSub.isInNextBox = false;
                if (activeSub.placeDateRaw && activeSub.frequency_days) {
                  const placeDate = parseOgPlaceDateUtc(place);
                  if (placeDate) {
                    const every = activeSub.every;
                    // Treat as monthly: add one month in UTC
                    placeDate.setUTCMonth(placeDate.getUTCMonth() + every);
                    // Format in UTC, always
                    activeSub.placeDateFormatted = placeDate.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    });
                    activeSub.placeDateRaw = placeDate.toISOString().split('T')[0];
                  }
                }
              }
            }

            // Refetch order + lines so nextOrders[0].total (and view-details modal) stay in sync
            await this.updateNextOrderObject();
          } else {
            // Handle error response
            const errorMessage = result.data?.message || result.error || 'Unknown error';
            console.error(`Failed to remove item: ${errorMessage}`);
            this.$dispatch('toast', { message: `Failed to remove item: ${errorMessage}`, type: 'error' });
          }
        } catch (error) {
          console.error(`Error removing item: ${error.message || 'Unknown error'}`);
          this.$dispatch('toast', {
            message: `Error removing item: ${error.message || 'Unknown error'}`,
            type: 'error',
          });
        } finally {
          // Always clear loading state
          this.nextOrderLoading = false;
        }
      } else {
        console.error('Error removing item: Missing item ID');
        this.$dispatch('toast', {
          message: 'Error removing item: Missing item ID',
          type: 'error',
        });
        // Always clear loading state
        this.nextOrderLoading = false;
      }
    },

    // Modal management methods
    handleCancelSubscriptionClick(item, shopID) {
      const locationHash = window.location.hash;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'dl_custom_sub_cancel_click',
        DSC: {
          subscription_id: item?.subscription ?? item?.public_id ?? null,
          item_name: item?.productObj?.name ?? null,
          item_variant_id: item?.product ?? null,
          quantity: item?.quantity ?? null,
          sub_frequency: `${item?.every ?? 1} month${(item?.every ?? 1) > 1 ? 's' : ''}`,
          location: locationHash ? window.location.pathname + locationHash : window.location.pathname,
          ship_date: item?.placeDateRaw ?? item?.place ?? null,
        },
      });

      // We are supporting the original cancel flow and the ProsperStack cancel flow. If the user is not on the ProsperStack test, open the original cancel flow modal.
      if (!window.dsc?.prosperstack) {
        this.openModal('account-cancelation-flow-modal', { item, shopID });
        return;
      }

      // ProsperStack is active: start fallback timer. If prosperstack:flow:session-started does not fire within the configured seconds, close the flow and open the original cancel modal.
      const fallbackSeconds = window.dsc?.prosperstack?.fallbackTimeoutSeconds ?? 5;
      const fallbackMs = Math.max(1, Number(fallbackSeconds)) * 1000;
      const onSessionStarted = () => {
        clearTimeout(fallbackTimeoutId);
        window.removeEventListener('prosperstack:flow:session-started', onSessionStarted);
      };
      const fallbackTimeoutId = window.setTimeout(() => {
        window.removeEventListener('prosperstack:flow:session-started', onSessionStarted);
        if (typeof window.dsc?.closeProsperStackFlow === 'function') {
          window.dsc.closeProsperStackFlow();
        }
        this.openModal('account-cancelation-flow-modal', { item, shopID });
      }, fallbackMs);
      window.addEventListener('prosperstack:flow:session-started', onSessionStarted);
    },

    openModal(modalId, params) {
      const modal = document.getElementById(modalId);
      if (!modal) {
        throw new Error(`Modal with ID '${modalId}' not found`);
      }

      // Store reference to current modal and the element that opened it
      this.lastFocusedElement = document.activeElement;
      this.currentModal = modal;
      this.modalOpen = modalId;

      if (modalId === 'account-date-modal') {
        // Track subscription edit date click (individual subscription)
        if (params.type === 'subscription' && params.subscriptionId) {
          const subscription = this.activeSubscriptions.find((sub) => sub.public_id === params.subscriptionId);
          if (subscription) {
            const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';

            // Store data for both click and confirm events
            window.dsc = window.dsc || {};
            window.dsc.editSubDateData = {
              location: locationHash,
              ship_date: subscription.placeDateRaw,
              subscription_id: subscription.public_id,
              item_name: subscription.productObj?.name || null,
              item_id: subscription.productObj?.sku || null,
              item_variant_id: subscription.productObj?.external_product_id || null,
              quantity: subscription.quantity,
              sub_frequency: subscription.every != null
                ? `${subscription.every} month${subscription.every > 1 ? 's' : ''}`
                : null,
            };

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'dl_custom_sub_edit_date_click',
              DSC: { ...window.dsc.editSubDateData },
            });
          }
        }

        if (params.action === 'editOrderDate' && params.nextOrderItems && params.nextOrderItems.length > 0) {
          // Track edit date click
          const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';
          const { nextBoxSubtotal, nextBoxSubs, itemsCondensed } = this._processNextOrderItemsForDataLayer(
            params.nextOrderItems,
          );

          // make this accessible for the confirm event
          window.dsc = window.dsc || {};
          window.dsc.editOrderDateData = {
            ship_date: params.nextOrderItems[0].place,
            ordergroove_public_order_id: params.orderId,
            subtotal_amount: nextBoxSubtotal,
            total_item_quantity: params.nextOrderItems.length,
            total_subscription_item_quantity: nextBoxSubs,
            items: itemsCondensed,
          };

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'dl_custom_order_edit_date_click',
            DSC: {
              location: locationHash,
              ship_date: params.nextOrderItems[0].place,
              ordergroove_public_order_id: params.orderId,
              subtotal_amount: nextBoxSubtotal,
              total_item_quantity: params.nextOrderItems.length,
              total_subscription_item_quantity: nextBoxSubs,
              items: itemsCondensed,
            },
          });
        }

        // Dispatch a global event on the window for the date-picker to catch
        const subscriptionsForOrder = this.activeSubscriptions.filter((sub) => sub.ogOrderId === params.orderId);

        window.dispatchEvent(
          new CustomEvent('selected-date', {
            detail: {
              type: params.type,
              subscriptionId: params.subscriptionId,
              orderId: params.orderId,
              date: params.placeDate,
              items: subscriptionsForOrder,
            },
          }),
        );
      }

      if (modalId === 'account-confirmation-modal') {
        // If skip confirmation, track click
        // TODO: Refactor to use _processNextOrderItemsForDataLayer
        if (params.action === 'skipOrder') {
          const nextOrderItems = params.nextOrderItems;
          const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';
          const nextBoxSubtotal = nextOrderItems
            .reduce((sum, item) => sum + parseFloat(item.total_price), 0)
            .toFixed(2);
          const nextBoxSubs = nextOrderItems.filter((item) => item.subscription !== null).length;
          const itemsCondensed = [];
          nextOrderItems.forEach((item) => {
            itemsCondensed.push({
              subscription_id: item.subscription,
              item_name: item.productObj.name,
              item_variant: item.productObj.name,
              item_variant_id: item.productObj.external_product_id,
              quantity: item.quantity,
              sub_frequency: item.every != null ? `${item.every} month${item.every > 1 ? 's' : ''}` : null,
              price: item.price,
            });
          });

          // make this accessible for the confirm event
          window.dsc = window.dsc || {};
          window.dsc.skipOrderData = {
            ship_date: nextOrderItems[0].place,
            ordergroove_public_order_id: params.orderId,
            subtotal_amount: nextBoxSubtotal,
            total_item_quantity: nextOrderItems.length,
            total_subscription_item_quantity: nextBoxSubs,
            items: itemsCondensed,
          };

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'dl_custom_order_skip_click',
            DSC: {
              location: locationHash,
              ship_date: nextOrderItems[0].place,
              ordergroove_public_order_id: params.orderId,
              subtotal_amount: nextBoxSubtotal,
              total_item_quantity: nextOrderItems.length,
              total_subscription_item_quantity: nextBoxSubs,
              items: itemsCondensed,
            },
          });
        }

        if (params.action === 'sendNow' && params.nextOrderItems && params.nextOrderItems.length > 0) {
          // Track send now click
          const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';
          const { nextBoxSubtotal, nextBoxSubs, itemsCondensed } = this._processNextOrderItemsForDataLayer(
            params.nextOrderItems,
          );

          // make this accessible for the confirm event
          window.dsc = window.dsc || {};
          window.dsc.sendNowData = {
            ship_date: params.nextOrderItems[0].place,
            ordergroove_public_order_id: params.orderId,
            subtotal_amount: nextBoxSubtotal,
            total_item_quantity: params.nextOrderItems.length,
            total_subscription_item_quantity: nextBoxSubs,
            items: itemsCondensed,
          };

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'dl_custom_order_send_now_click',
            DSC: {
              location: locationHash,
              ship_date: params.nextOrderItems[0].place,
              ordergroove_public_order_id: params.orderId,
              subtotal_amount: nextBoxSubtotal,
              total_item_quantity: params.nextOrderItems.length,
              total_subscription_item_quantity: nextBoxSubs,
              items: itemsCondensed,
            },
          });
        }

        window.dispatchEvent(
          new CustomEvent('set-confirmation-modal', {
            detail: { orderId: params.orderId, action: params.action, message: params.message, title: params.title },
          }),
        );
      }

      if (modalId === 'account-cancelation-flow-modal') {
        window.dispatchEvent(
          new CustomEvent('selected-item-to-swap-in-cancelation-flow', {
            detail: { item: params.item, step: 'start', shopID: params.shopID },
          }),
        );
      }

      if (modalId === 'account-swap-products-modal') {
        window.dispatchEvent(new CustomEvent('selected-item-to-swap', { detail: { item: params.item } }));
      }
      // Open the modal
      modal.open(document.activeElement);
    },

    closeModal(modalId = null) {
      let modal;
      if (modalId) {
        modal = document.getElementById(modalId);
      } else if (this.currentModal) {
        modal = this.currentModal;
      }

      if (modal && typeof modal.close === 'function') {
        modal.close();
        this.modalOpen = null;
        if (this.lastFocusedElement) {
          this.lastFocusedElement.focus();
        }
        this.currentModal = null;
      }
    },

    // Update quantity immediately in global nextOrderItems for UI reactivity
    updateItemQuantity(subscriptionId, newQuantity) {
      // Update in nextOrderItems
      let item = this.nextOrderItems.find((item) => item.subscription === subscriptionId);
      if (!item) {
        item = this.nextOrderItems.find((item) => item.public_id === subscriptionId);
      }
      if (item) {
        item.quantity = parseInt(newQuantity);
        item.total_price = (parseFloat(item.price) * parseInt(newQuantity)).toFixed(2);
      }
      // Update in subscriptions
      let subscription = this.activeSubscriptions.find((subscription) => subscription.public_id === subscriptionId);
      if (subscription) {
        subscription.quantity = parseInt(newQuantity);
        subscription.total_price = (parseFloat(subscription.final_unit_price) * parseInt(newQuantity)).toFixed(2);
        subscription.final_price = (parseFloat(subscription.final_unit_price) * parseInt(newQuantity)).toFixed(2);
      }
      // Update in activeSubscriptions
      let activeSub = this.activeSubscriptions.find((subscription) => subscription.public_id === subscriptionId);
      if (activeSub) {
        activeSub.quantity = parseInt(newQuantity);
        activeSub.total_price = (parseFloat(activeSub.final_unit_price) * parseInt(newQuantity)).toFixed(2);
        activeSub.final_price = (parseFloat(activeSub.final_unit_price) * parseInt(newQuantity)).toFixed(2);
      }
    },

    updateItemFrequency(subscriptionId, newFrequency) {
      // Update in nextOrderItems
      let item = this.nextOrderItems.find((item) => item.subscription === subscriptionId);
      if (!item) {
        item = this.nextOrderItems.find((item) => item.public_id === subscriptionId);
      }
      if (item) {
        item.every = parseInt(newFrequency);
      }
      // Update in subscriptions
      let subscription = this.allSubscriptions.find((subscription) => subscription.public_id === subscriptionId);
      if (subscription) {
        subscription.every = parseInt(newFrequency);
      }
      // Update in activeSubscriptions
      let activeSub = this.activeSubscriptions.find((subscription) => subscription.public_id === subscriptionId);
      if (activeSub) {
        activeSub.every = parseInt(newFrequency);
      }
    },
    async changeSubscriptionQuantity(subscriptionId, quantity) {
      // Prevent concurrent calls
      if (this._inflightRequests.has(`changeQty-${subscriptionId}`)) {
        console.log('Quantity change already in progress');
        return;
      }

      // Update UI immediately
      this.updateItemQuantity(subscriptionId, quantity);

      try {
        const result = await changeOGSubscriptionQuantity(subscriptionId, quantity);
        if (result.success) {
          this.$dispatch('toast', {
            message: window.translations.customer.global.quantity_updated,
            type: 'success',
            product: this.activeSubscriptions.find((sub) => sub.public_id === subscriptionId).productObj,
          });
          await this.updateNextOrderObject();
        } else {
          alert(
            window.translations.customer.alerts.quantity_update_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
          // Revert the optimistic update on failure
          await this.updateNextOrderObject();
        }
      } catch (error) {
        // Revert the optimistic update on error
        await this.updateNextOrderObject();
        throw new Error('Error changing subscription quantity:', error);
      }
    },

    async changeItemQuantity(itemId, quantity) {
      // Prevent concurrent calls
      if (this._inflightRequests.has(`changeQty-${itemId}`)) {
        console.log('Quantity change already in progress');
        return;
      }

      // Update UI immediately
      this.updateItemQuantity(itemId, quantity);

      try {
        const result = await changeOGItemQuantity(itemId, quantity);
        if (result.success) {
          this.$dispatch('toast', {
            message: window.translations.customer.global.quantity_updated,
            type: 'success',
            product: this.nextOrderItems.find((item) => item.public_id === itemId).productObj,
          });
          await this.updateNextOrderObject();
        } else {
          alert(
            window.translations.customer.alerts.quantity_update_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
          // Revert the optimistic update on failure
          await this.updateNextOrderObject();
        }
      } catch (error) {
        // Revert the optimistic update on error
        await this.updateNextOrderObject();
        throw new Error('Error changing item quantity:', error);
      }
    },

    /**
     * @param {string} subscriptionId - OG subscription public id
     * @param {string|number} frequency - New frequency (months); may match `item.every` after x-model updates
     * @param {string|number} [previousEvery] - Frequency before this change; pass `item._prevEveryForDl` from templates that use x-model on `item.every` (see Liquid @focus / @change). Without it, sub_frequency can match new_sub_frequency because x-model mutates before @change.
     */
    async changeSubscriptionFrequency(subscriptionId, frequency, previousEvery) {
      // Prevent concurrent calls
      if (this._inflightRequests.has(`changeQty-${subscriptionId}`)) {
        console.log('Quantity change already in progress');
        return;
      }

      const subscription =
        this.activeSubscriptions.find((sub) => sub.public_id === subscriptionId) ||
        this.allSubscriptions.find((sub) => sub.public_id === subscriptionId);

      const locationHash = window.location.hash !== '' ? window.location.hash : '#dashboard';
      const formatSubFrequency = (every) => {
        if (every == null || every === '') return null;
        const n = parseInt(String(every), 10);
        if (Number.isNaN(n)) return null;
        return `${n} month${n > 1 ? 's' : ''}`;
      };

      const newEveryParsed = parseInt(String(frequency), 10);
      // `subscription.every` is already the NEW value here (Alpine x-model runs before @change),
      // so we rely on `previousEvery` (captured via @focus in Liquid) for the prior frequency.
      const oldEveryForDl = previousEvery ?? subscription?._prevEveryForDl;
      pushDscDataLayerEvent('dl_custom_sub_frequency_change', {
        location: locationHash,
        subscription_id: String(subscriptionId),
        item_name: subscription?.productObj?.name || null,
        item_id: subscription?.productObj?.sku || null,
        item_variant_id: subscription?.productObj?.external_product_id
          ? String(subscription.productObj.external_product_id)
          : null,
        quantity: subscription?.quantity ?? null,
        sub_frequency: formatSubFrequency(oldEveryForDl),
        new_sub_frequency: Number.isNaN(newEveryParsed) ? null : formatSubFrequency(newEveryParsed),
      });

      // Update UI immediately
      this.updateItemFrequency(subscriptionId, frequency);
      try {
        const result = await changeOGSubscriptionFrequency(subscriptionId, frequency);
        if (result.success) {
          this.$dispatch('toast', {
            message: window.translations.customer.global.frequency_updated,
            type: 'success',
            product: this.activeSubscriptions.find((sub) => sub.public_id === subscriptionId).productObj,
          });
          await this.updateNextOrderObject();
        } else {
          alert(
            window.translations.customer.alerts.frequency_update_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
        }
      } catch (error) {
        throw new Error('Error changing subscription frequency:', error);
      }
    },

    async reactivateSubscription(subscriptionId, frequency) {
      try {
        this.loading = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const result = await reactivateOGSubscription(subscriptionId, frequency);
        if (result.success) {
          this.$dispatch('toast', {
            message: window.translations.customer.global.subscription_reactivated,
            type: 'success',
            product: this.inactiveSubscriptions.find((sub) => sub.public_id === subscriptionId).productObj,
          });
          await this.fetchCustomerInfo();
        } else {
          alert(
            window.translations.customer.alerts.reactivate_failed.replace(
              '{{ error }}',
              result.data?.message || 'Unknown error',
            ),
          );
        }
      } catch (error) {
        throw new Error('Error reactivating subscription:', error);
      } finally {
        this.loading = false;
        this.nextBoxLoading = false;
      }
    },

    formattedShippingAddress(address) {
      if (!address) return '';
      return `${address.first_name} ${address.last_name}, ${address.address}, ${address.city}, ${address.state_province_code} ${address.zip_postal_code}`;
    },

    async sendPaymentEditEmail() {
      const result = await sendPaymentUpdateEmail();
      if (result.success) {
        this.openModal('account-payment');
      } else {
        alert(
          window.translations.customer.alerts.payment_update_email_failed.replace(
            '{{ error }}',
            result.data?.message || 'Unknown error',
          ),
        );
      }
    },

    orderDetailsCacheIsComplete(cachedOrder) {
      return Boolean(
        cachedOrder &&
          Array.isArray(cachedOrder.line_items) &&
          cachedOrder.line_items.length > 0 &&
          cachedOrder.shipping_address,
      );
    },

    getCachedOrderDetails(orderId = null, customerOrderUrl = '') {
      const detailsById = window.dsc?.orderDetailsById || {};
      const urlToId = window.dsc?.orderDetailsUrlToId || {};

      const resolvedOrderId = orderId || (customerOrderUrl ? urlToId[customerOrderUrl] : null);
      if (!resolvedOrderId) {
        return null;
      }

      return detailsById[String(resolvedOrderId)] || detailsById[resolvedOrderId] || null;
    },

    async showOrderDetails(customerOrderUrl = '', orderId = null) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.orderDetailsLoading = true;
      this.order = null; // Reset previous order
      const cachedOrder = this.getCachedOrderDetails(orderId, customerOrderUrl);
      const resolvedCustomerOrderUrl = customerOrderUrl || cachedOrder?.customer_order_url || '';

      try {
        if (cachedOrder && this.orderDetailsCacheIsComplete(cachedOrder)) {
          this.order = cachedOrder;
          const resolvedOrderId = orderId || cachedOrder?.id || null;
          const url = new URL(window.location.href);
          if (resolvedOrderId) {
            url.searchParams.set('orderId', resolvedOrderId);
          }
          url.hash = 'order-details';
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          this.$dispatch('change-tab', { tab: 'order-details' });
          return;
        }

        let fetchedOrder = null;

        // customer_order_url commonly points to Shopify-hosted account pages on another origin.
        // Skip cross-origin fetches to avoid noisy 406s and navigate there if no cached data exists.
        if (resolvedCustomerOrderUrl) {
          const parsedUrl = new URL(resolvedCustomerOrderUrl, window.location.origin);
          if (parsedUrl.origin !== window.location.origin) {
            window.location.assign(resolvedCustomerOrderUrl);
            return;
          }

          parsedUrl.searchParams.set('view', 'json');

          const response = await fetch(parsedUrl.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
              Accept: 'application/json, text/html',
            },
          });
          const contentType = response.headers.get('content-type') || '';
          if (response.ok && contentType.includes('application/json')) {
            const data = await response.json();
            fetchedOrder = data?.order || (data?.id ? data : null);
            if (fetchedOrder?.id) {
              window.dsc = window.dsc || {};
              window.dsc.orderDetailsById = window.dsc.orderDetailsById || {};
              window.dsc.orderDetailsById[String(fetchedOrder.id)] = fetchedOrder;
              const orderPageUrl = fetchedOrder.customer_order_url || fetchedOrder.customer_url;
              if (orderPageUrl) {
                window.dsc.orderDetailsUrlToId = window.dsc.orderDetailsUrlToId || {};
                window.dsc.orderDetailsUrlToId[orderPageUrl] = fetchedOrder.id;
              }
            }
          }
        }

        this.order = fetchedOrder;

        if (this.order) {
          const resolvedOrderId =
            orderId ||
            this.order?.id ||
            window.dsc?.orderDetailsUrlToId?.[customerOrderUrl] ||
            window.dsc?.orderDetailsUrlToId?.[resolvedCustomerOrderUrl] ||
            null;
          const url = new URL(window.location.href);
          if (resolvedOrderId) {
            url.searchParams.set('orderId', resolvedOrderId);
          }
          url.hash = 'order-details';
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          this.$dispatch('change-tab', { tab: 'order-details' });
          return;
        }

        if (resolvedCustomerOrderUrl) {
          window.location.assign(resolvedCustomerOrderUrl);
          return;
        }

        if (orderId || customerOrderUrl) {
          const msg =
            window.translations.customer.order?.not_found || 'Order not found.';
          this.$dispatch('toast', { type: 'error', message: msg });
          this.$dispatch('change-tab', { tab: 'order-history' });
          const url = new URL(window.location.href);
          url.searchParams.delete('orderId');
          if (!url.search || url.search === '?') {
            url.search = '';
          }
          url.hash = '';
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }
      } catch (error) {
        this.$dispatch('toast', {
          type: 'error',
          message:
            error.message ||
            (window.translations.customer.order?.fetch_details_failed || 'Failed to load order details.').replace(
              '{{ error }}',
              error.message,
            ),
        });
      } finally {
        this.orderDetailsLoading = false;
      }
    },
  };
}
